/**
 * Cover Studio — F-09 per UX mockup
 * Shows 3 cover options, generate button, set-as-primary, download
 * Requires an active subscription — free-tier users see UpgradeModal instead.
 */
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGenerateCovers, useCovers, useSetPrimaryCover } from '../services/covers';
import type { Cover } from '../services/covers';
import { useBook } from '../hooks/useBooks';
import { Navbar } from '../components/layout/Navbar';
import { useUserProfile, isActiveSubscriber } from '../hooks/useUserProfile';
import { UpgradeModal } from '../components/ui/UpgradeModal';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';

export function CoverStudio() {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();

  // Entitlement — all hooks called unconditionally (Rules of Hooks)
  const { data: profile, isLoading: profileLoading } = useUserProfile();

  // Persona check — same queryKey as NewBook.tsx so React Query deduplicates
  const { data: personasData, isLoading: personasLoading } = useQuery({
    queryKey: ['personas'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<{ personas: Array<{ id: string; penName: string }> }>>('/personas');
      return res.data.data ?? null;
    },
  });
  const hasPersona = (personasData?.personas?.length ?? 0) > 0;

  const { data: book } = useBook(bookId);
  const generateCovers = useGenerateCovers(bookId ?? '');
  const { data: existingCovers, isLoading: coversLoading } = useCovers(bookId ?? '');
  const setPrimary = useSetPrimaryCover(bookId ?? '');

  const [covers, setCovers] = useState<Cover[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [primarySaved, setPrimarySaved] = useState(false);

  // Restore covers from Firestore on mount
  useEffect(() => {
    if (existingCovers && existingCovers.length > 0 && covers.length === 0) {
      setCovers(existingCovers);
      setSelectedIndex(0);
    }
  }, [existingCovers, covers.length]);

  const handleGenerate = () => {
    setError(null);
    setPrimarySaved(false);
    generateCovers.mutate(undefined, {
      onSuccess: (data) => {
        setCovers(data.covers);
        setSelectedIndex(data.covers.length > 0 ? 0 : null);
      },
      onError: (err: unknown) => {
        const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
        setError(axiosErr.response?.data?.error?.message ?? 'Cover generation failed. Please try again.');
      },
    });
  };

  const handleSetPrimary = () => {
    if (selectedIndex === null || !covers[selectedIndex]) return;
    const cover = covers[selectedIndex];
    setPrimary.mutate(cover.id, {
      onSuccess: () => setPrimarySaved(true),
      onError: () => setError('Failed to set primary cover. Please try again.'),
    });
  };

  const handleDownload = () => {
    if (selectedIndex === null || !covers[selectedIndex]) return;
    window.open(covers[selectedIndex].publicUrl, '_blank');
  };

  const isLoading = coversLoading;

  // ── Subscription gate ────────────────────────────────────────────────────
  // Wait until profile loads so we don't flash the wrong UI.
  if (profileLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar variant="authed" />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  // Free-tier users see the upgrade modal, not the Cover Studio UI.
  if (!isActiveSubscriber(profile)) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar variant="authed" />
        <UpgradeModal
          open={true}
          onClose={() => void navigate('/dashboard')}
        />
      </div>
    );
  }
  // ── End subscription gate ─────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="authed" />

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-6">
          <Link to={`/books/${bookId}`} className="text-muted-foreground hover:text-foreground transition-colors">
            ← {book?.title ?? 'Book'}
          </Link>
          <span className="text-muted-foreground">/</span>
          <span className="text-foreground font-medium">Cover Studio</span>
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-1">Design Your Cover</h1>
        <p className="text-muted-foreground text-sm mb-8">
          3 AI-generated cover variations · KDP-compliant dimensions · Your title &amp; pen name overlaid
        </p>

        {isLoading && (
          <div className="text-center py-16 border border-border rounded-xl bg-card">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground text-sm">Loading your covers…</p>
          </div>
        )}

        {!isLoading && covers.length === 0 && !generateCovers.isPending && (
          <div className="text-center py-16 border border-border rounded-xl bg-card">
            <div className="text-5xl mb-4">🎨</div>
            <h2 className="text-lg font-bold text-foreground mb-2">Generate your cover designs</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              AI will create 3 unique cover variations based on your book's genre and premise.
            </p>

            {!personasLoading && !hasPersona ? (
              /* Persona required notice */
              <div className="inline-flex items-start gap-2 px-4 py-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-sm text-amber-300 max-w-sm mx-auto text-left">
                <span className="mt-0.5 shrink-0">⚠️</span>
                <p>
                  You need an author persona to generate a cover.{' '}
                  <Link
                    to="/persona"
                    className="underline underline-offset-2 font-medium hover:text-amber-100 transition-colors"
                  >
                    Create your author persona
                  </Link>{' '}
                  first.
                </p>
              </div>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={generateCovers.isPending || personasLoading}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50"
              >
                Generate 3 Covers
              </button>
            )}

            {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
          </div>
        )}

        {generateCovers.isPending && (
          <div className="text-center py-16 border border-border rounded-xl bg-card">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-foreground font-medium">Generating your covers…</p>
            <p className="text-sm text-muted-foreground mt-1">This usually takes 30–60 seconds</p>
          </div>
        )}

        {!isLoading && covers.length > 0 && (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Cover selection grid */}
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">Choose Your Cover</p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {covers.map((cover, i) => (
                  <button
                    key={cover.id}
                    onClick={() => { setSelectedIndex(i); setPrimarySaved(false); }}
                    className={`aspect-[2/3] rounded-md overflow-hidden border-2 transition-all ${
                      selectedIndex === i
                        ? 'border-primary ring-2 ring-primary/25'
                        : 'border-transparent hover:border-primary/40'
                    }`}
                  >
                    <img
                      src={cover.publicUrl}
                      alt={`Cover option ${i + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </button>
                ))}
              </div>

              {!personasLoading && !hasPersona && (
                <div className="flex items-start gap-2 mb-2 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-xs text-amber-300">
                  <span className="mt-0.5 shrink-0">⚠️</span>
                  <p>
                    You need an author persona to regenerate covers.{' '}
                    <Link
                      to="/persona"
                      className="underline underline-offset-2 font-medium hover:text-amber-100 transition-colors"
                    >
                      Create your author persona
                    </Link>
                    .
                  </p>
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={generateCovers.isPending || !hasPersona || personasLoading}
                className="w-full mb-2 px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🔄 Try 3 new designs
              </button>

              {/* Set as Primary */}
              <button
                onClick={handleSetPrimary}
                disabled={selectedIndex === null || setPrimary.isPending}
                className="w-full mb-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {setPrimary.isPending ? (
                  <>
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Saving…
                  </>
                ) : primarySaved ? (
                  '✓ Set as Primary'
                ) : (
                  '⭐ Set as Primary Cover'
                )}
              </button>

              {/* Download */}
              <button
                onClick={handleDownload}
                disabled={selectedIndex === null}
                className="w-full px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm disabled:opacity-50"
              >
                ↓ Download this cover
              </button>

              {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
            </div>

            {/* Preview */}
            {selectedIndex !== null && covers[selectedIndex] && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-3">Preview</p>
                <div className="w-40 mx-auto">
                  <img
                    src={covers[selectedIndex].publicUrl}
                    alt="Selected cover preview"
                    className="w-full rounded-lg shadow-2xl"
                  />
                </div>
                {book && (
                  <div className="mt-4 text-center">
                    <p className="text-sm font-bold text-foreground">{book.title}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {primarySaved && (
          <div className="mt-6 p-4 bg-green-900/20 border border-green-500/25 rounded-md flex items-center gap-3">
            <span className="text-green-400">✓</span>
            <div>
              <p className="text-sm font-medium text-green-400">Primary cover saved!</p>
              <p className="text-xs text-muted-foreground">Your cover selection is saved. Next step: KDP Walkthrough.</p>
            </div>
            <Link
              to={`/kdp-walkthrough?bookId=${bookId}`}
              className="ml-auto text-sm text-primary hover:underline"
            >
              KDP Walkthrough →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
